(() => {
  "use strict";

  const config = window.CourtCurrent || {};
  const routes = config.routes || {};
  const liveRegion = document.getElementById("ThemeLiveRegion");
  const COMPARE_KEY = "court-current-compare-v1";
  const RECENT_KEY = "court-current-recent-v1";
  const MAX_COMPARE = 4;
  const focusableSelector =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = "";
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 30);
  }

  function track(eventName, detail = {}) {
    if (config.analyticsEnabled !== true) return;
    const payload = {
      event: eventName,
      currency: config.page?.currency || "USD",
      ...detail,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent("court-current:event", { detail: payload }),
    );
  }

  window.CourtCurrent = {
    ...config,
    track,
  };

  function escapeHTML(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  }

  function money(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: config.page?.currency || "USD",
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function setExpanded(button, open) {
    if (button) button.setAttribute("aria-expanded", String(open));
  }

  function openLayer(layer, trigger, closeSelector, preferredFocus) {
    if (!layer) return;
    layer.hidden = false;
    layer.dataset.open = "true";
    document.body.classList.add("is-locked");
    setExpanded(trigger, true);

    const close = () => {
      layer.hidden = true;
      delete layer.dataset.open;
      document.body.classList.remove("is-locked");
      setExpanded(trigger, false);
      document.removeEventListener("keydown", onKeydown);
      trigger?.focus();
    };

    const onKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...layer.querySelectorAll(focusableSelector)].filter(
        (element) => !element.hasAttribute("hidden"),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    layer.querySelectorAll(closeSelector).forEach((control) => {
      control.addEventListener("click", close, { once: true });
    });
    document.addEventListener("keydown", onKeydown);
    window.setTimeout(() => {
      (preferredFocus || layer.querySelector(focusableSelector))?.focus();
    }, 20);
    return close;
  }

  function setupDrawers() {
    const mobileTrigger = document.querySelector("[data-mobile-nav-open]");
    const mobileLayer = document.querySelector("[data-mobile-nav]");
    mobileTrigger?.addEventListener("click", () =>
      openLayer(mobileLayer, mobileTrigger, "[data-mobile-nav-close]"),
    );

    const searchTrigger = document.querySelector("[data-search-open]");
    const searchLayer = document.querySelector("[data-search-drawer]");
    const searchInput = searchLayer?.querySelector("[data-predictive-input]");
    searchTrigger?.addEventListener("click", () =>
      openLayer(
        searchLayer,
        searchTrigger,
        "[data-search-close]",
        searchInput,
      ),
    );

    const facetsTrigger = document.querySelector("[data-facets-open]");
    const facetsLayer = document.querySelector("[data-facets-drawer]");
    let closeFacets;
    facetsTrigger?.addEventListener("click", () => {
      closeFacets = openLayer(
        facetsLayer,
        facetsTrigger,
        "[data-facets-close]",
        facetsLayer?.querySelector("[data-filter-input]"),
      );
    });
    window.addEventListener("resize", () => {
      if (window.innerWidth > 999 && facetsLayer) {
        if (facetsLayer.dataset.open === "true") closeFacets?.();
        facetsLayer.hidden = false;
        delete facetsLayer.dataset.open;
        document.body.classList.remove("is-locked");
        setExpanded(facetsTrigger, false);
      }
    });
  }

  function setupPredictiveSearch() {
    const form = document.querySelector("[data-predictive-search]");
    const input = form?.querySelector("[data-predictive-input]");
    const results = form
      ?.closest("[data-search-drawer]")
      ?.querySelector("[data-predictive-results]");
    if (!form || !input || !results) return;

    let controller;
    const search = debounce(async () => {
      const query = input.value.trim();
      if (query.length < 2) {
        results.innerHTML =
          '<p class="predictive-results__hint">Enter at least two characters.</p>';
        return;
      }

      controller?.abort();
      controller = new AbortController();
      results.innerHTML =
        '<p class="predictive-results__hint">Searching…</p>';
      try {
        const base = routes.searchSuggest || "/search/suggest";
        const params = new URLSearchParams({
          q: query,
          "resources[type]": "product,collection,page,article",
          "resources[limit]": "8",
          "resources[options][unavailable_products]": "hide",
          section_id: "predictive-search",
        });
        const response = await fetch(`${base}?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error("Search request failed");
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        const section = documentFragment.querySelector(
          "#shopify-section-predictive-search",
        );
        if (!section) throw new Error("Predictive search section unavailable");
        results.innerHTML = section.innerHTML;
      } catch (error) {
        if (error.name === "AbortError") return;
        results.innerHTML =
          '<p class="predictive-results__hint">Search is temporarily unavailable. Submit the form for full results.</p>';
      }
    }, 180);

    input.addEventListener("input", search);
    form.addEventListener("submit", () => {
      const query = input.value.trim();
      track("search_performed", {
        query_length: Array.from(query).length,
        result_count: results.querySelectorAll('[role="option"]').length,
      });
    });
  }

  async function getCart() {
    const base = routes.cart || "/cart";
    const response = await fetch(`${base}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Unable to load cart");
    return response.json();
  }

  function updateCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = String(count);
      element.setAttribute("aria-label", `${count} items`);
    });
  }

  function variantSwatchFill(label) {
    const value = String(label || "").toLowerCase();
    if (value.includes("mixed") || value.includes("multi")) {
      return "conic-gradient(#2f6fb3, #d85d69, #e8c84b, #4c9162, #2f6fb3)";
    }
    const colors = [
      ["peacock blue", "#087a82"],
      ["dark blue", "#173b67"],
      ["light blue", "#78b6d4"],
      ["rose red", "#c64061"],
      ["dark gray", "#4f5559"],
      ["dark grey", "#4f5559"],
      ["light gray", "#b9bcbd"],
      ["light grey", "#b9bcbd"],
      ["light green", "#9dca68"],
      ["dark green", "#285c49"],
      ["navy", "#1d2f4f"],
      ["cyan", "#39bad5"],
      ["blue", "#2f70b7"],
      ["gray", "#858b8f"],
      ["grey", "#858b8f"],
      ["red", "#cf4148"],
      ["pink", "#e78ba8"],
      ["purple", "#7555a6"],
      ["orange", "#e77938"],
      ["yellow", "#e8d34d"],
      ["lime", "#b7d93d"],
      ["green", "#4e9363"],
      ["beige", "#d8c6a7"],
      ["khaki", "#b49d72"],
      ["brown", "#765447"],
      ["white", "#f8f7f2"],
      ["silver", "linear-gradient(135deg, #f4f6f6, #92999d)"],
      ["gold", "linear-gradient(135deg, #f0d27a, #ad7c28)"],
      ["black", "#191b1c"],
    ];
    return colors.find(([name]) => value.includes(name))?.[1] || null;
  }

  function combinedVariantParts(optionName, label) {
    const option = String(optionName || "").toLowerCase();
    const value = String(label || "").trim();
    const hasColor = option.includes("color") || option.includes("colour");
    let match;

    if (option.includes("width") && (hasColor || option.includes("finish"))) {
      match = value.match(
        /^(\d+(?:\.\d+)?)\s*mm\s*(?:-\s*)?(.+?)(?:\s*\((fits[^)]+)\))?$/i,
      );
      if (match) {
        return {
          group: `${match[1]} mm`,
          groupMeta: match[3] || "",
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
          sort: Number(match[1]),
        };
      }
    }

    if (option.includes("pack") && hasColor) {
      match = value.match(/^(\d+)\s*pack\s*-\s*(.+)$/i);
      if (match) {
        return {
          group: `${match[1]} Pack`,
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
          sort: Number(match[1]),
        };
      }
    }

    if (option.includes("thickness") && (hasColor || option.includes("design"))) {
      match = value.match(/^(.+?)\s*-\s*(\d+(?:\.\d+)?)\s*mm$/i);
      if (match) {
        return {
          group: `${match[2]} mm`,
          label: match[1].trim(),
          fill: hasColor ? variantSwatchFill(match[1]) : null,
          sort: Number(match[2]),
        };
      }
    }

    if (option.includes("style") && hasColor) {
      match = value.match(/^(.+?)\s*-\s*(.+)$/i);
      if (match) {
        return {
          group: match[1].trim(),
          label: match[2].trim(),
          fill: variantSwatchFill(match[2]),
        };
      }
    }

    if (option.includes("set") && hasColor) {
      match = value.match(/^(.+?)(?:\s*\(([^)]*set)\))?$/i);
      if (match) {
        return {
          group: match[2] || "Paddle only",
          label: match[1].trim(),
          fill: variantSwatchFill(match[1]),
          sort: match[2] ? 1 : 0,
        };
      }
    }

    if (option.includes("type") && option.includes("pack")) {
      match = value.match(/^((?:indoor|outdoor)\s+\d+-hole),\s*(\d+)\s*pack$/i);
      if (match) {
        return {
          group: match[1],
          label: `${match[2]} Pack`,
          fill: null,
          sort: Number(match[2]),
        };
      }
    }

    return null;
  }

  function enhanceCombinedVariantPicker(group) {
    if (!group || group.dataset.grouped === "true") return;
    const optionName = group.dataset.optionName || "";
    const buttons = [...group.querySelectorAll("[data-variant-pill]")];
    if (buttons.length < 3) return;

    const parsed = buttons.map((button, index) => {
      const original = button.dataset.variantLabel || button.textContent.trim();
      const parts = combinedVariantParts(optionName, original);
      return parts ? { button, index, original, ...parts } : null;
    });
    if (parsed.some((item) => !item)) return;

    const grouped = new Map();
    parsed.forEach((item) => {
      if (!grouped.has(item.group)) {
        grouped.set(item.group, {
          label: item.group,
          meta: item.groupMeta || "",
          sort: item.sort,
          firstIndex: item.index,
          items: [],
        });
      }
      const entry = grouped.get(item.group);
      if (!entry.meta && item.groupMeta) entry.meta = item.groupMeta;
      entry.items.push(item);
    });
    if (grouped.size < 2) return;

    const groups = [...grouped.values()].sort((a, b) => {
      if (Number.isFinite(a.sort) && Number.isFinite(b.sort)) {
        return a.sort - b.sort;
      }
      return a.firstIndex - b.firstIndex;
    });
    groups.forEach((entry) => {
      entry.items.sort((a, b) => {
        if (Number.isFinite(a.sort) && Number.isFinite(b.sort)) {
          return a.sort - b.sort;
        }
        return a.index - b.index;
      });
    });
    const fragment = document.createDocumentFragment();

    groups.forEach((entry, groupIndex) => {
      const section = document.createElement("div");
      section.className = "variant-option-group";
      const heading = document.createElement("div");
      heading.className = "variant-option-group__heading";
      heading.id = `VariantGroup-${group.dataset.target}-${groupIndex}`;

      const title = document.createElement("strong");
      title.textContent = entry.label;
      heading.appendChild(title);
      if (entry.meta) {
        const meta = document.createElement("span");
        meta.textContent = entry.meta.replace(/^fits\b/i, "Fits");
        heading.appendChild(meta);
      }

      const choices = document.createElement("div");
      choices.className = "variant-option-group__choices";
      entry.items.forEach((item) => {
        const button = item.button;
        button.classList.add("variant-pill--grouped");
        button.setAttribute("aria-label", item.original);
        button.setAttribute("aria-describedby", heading.id);
        button.title = item.original;
        button.textContent = "";
        if (item.fill) {
          const swatch = document.createElement("span");
          swatch.className = "variant-pill__swatch";
          swatch.style.setProperty("--swatch-fill", item.fill);
          swatch.setAttribute("aria-hidden", "true");
          button.appendChild(swatch);
        }
        const text = document.createElement("span");
        text.textContent = item.label;
        button.appendChild(text);
        choices.appendChild(button);
      });

      section.append(heading, choices);
      fragment.appendChild(section);
    });

    group.textContent = "";
    group.appendChild(fragment);
    group.classList.add("variant-pills--grouped");
    group.dataset.grouped = "true";
  }

  async function addToCart(form) {
    const message = form.querySelector("[data-product-form-message]");
    const button = form.querySelector("[data-add-to-cart],button[type='submit']");
    const previousLabel = button?.innerHTML;
    const buttonWasDisabled = button?.disabled || false;
    const productRoot = form.closest("[data-product-root]");
    const merchantBlocked =
      productRoot?.dataset.merchantBlocked === "true";
    if (merchantBlocked) {
      const text =
        "This product cannot be added until merchant review is complete.";
      announce(text);
      if (message) message.textContent = text;
      return;
    }
    if (button) {
      button.disabled = true;
      button.textContent = "Adding…";
    }
    if (message) message.textContent = "";

    try {
      const response = await fetch(routes.cartAdd || "/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || "This item could not be added.");
      }
      const cart = await getCart();
      updateCartCount(cart.item_count);
      const quantity = Number(new FormData(form).get("quantity") || 1);
      announce(`${payload.product_title || "Product"} added to cart.`);
      if (message) {
        message.innerHTML = `Added to cart. <a href="${
          routes.cart || "/cart"
        }">View cart</a>`;
      }
      track("product_added_to_cart", {
        product_id: String(payload.product_id || ""),
        variant_id: String(payload.variant_id || ""),
        quantity,
        value: Number(payload.final_price || payload.price || 0) / 100,
      });
    } catch (error) {
      const text = error.message || "This item could not be added.";
      announce(text);
      if (message) message.textContent = text;
    } finally {
      if (button) {
        button.disabled = buttonWasDisabled;
        button.innerHTML = previousLabel;
      }
    }
  }

  function setupProductForms() {
    document
      .querySelectorAll(".variant-pills[data-variant-pills]")
      .forEach(enhanceCombinedVariantPicker);

    document
      .querySelectorAll("[data-product-form],[data-product-card-form]")
      .forEach((form) => {
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          addToCart(form);
        });
      });

    document.querySelectorAll("[data-variant-select]").forEach((select) => {
      select.addEventListener("change", () => {
        const option = select.selectedOptions[0];
        const root = select.closest("[data-product-root]");
        const merchantBlocked = root?.dataset.merchantBlocked === "true";
        const variantAvailable = option?.dataset.available === "true";
        const available = variantAvailable && !merchantBlocked;
        const price = option?.dataset.priceFormatted || "";
        const comparePrice = Number(option?.dataset.comparePrice || 0);
        const currentPrice = Number(option?.dataset.price || 0);

        root?.querySelectorAll("[data-product-price]").forEach((node) => {
          node.textContent = price;
        });
        const compare = root?.querySelector("[data-product-compare-price]");
        if (compare) {
          compare.textContent = option?.dataset.compareFormatted || "";
          compare.hidden = !(comparePrice > currentPrice);
        }
        root?.querySelectorAll("[data-add-to-cart]").forEach((button) => {
          button.disabled = !available;
          const label = button.querySelector("[data-add-to-cart-label]");
          if (label) {
            label.textContent = merchantBlocked
              ? "Currently unavailable"
              : available
                ? "Add to cart"
                : "Unavailable";
          }
        });
        document
          .querySelectorAll("[data-sticky-price]")
          .forEach((node) => (node.textContent = price));
        document
          .querySelectorAll("[data-sticky-submit]")
          .forEach((button) => {
            button.disabled = !available;
            button.textContent = merchantBlocked
              ? "Currently unavailable"
              : available
                ? "Add to cart"
                : "Unavailable";
          });
        const availability = root?.querySelector("[data-variant-availability]");
        if (availability) {
          availability.classList.toggle(
            "inventory-status--unavailable",
            !available,
          );
          availability.querySelector("strong").textContent = merchantBlocked
            ? "Not offered for sale"
            : available
              ? "Available"
              : "Currently unavailable";
        }
        if (option?.dataset.url) {
          window.history.replaceState({}, "", option.dataset.url);
        }

        // Swap the main gallery image to this variant's own media. Variants
        // without an assigned image leave the gallery where it is - jumping to
        // an unrelated slide would be worse than not moving.
        const variantMediaId = option?.dataset.mediaId;
        if (variantMediaId && selectProductMedia) {
          selectProductMedia(variantMediaId, false);
        }

        // Keep the pill row in step when the select changes for any reason.
        const pills = root?.querySelector("[data-variant-pills]");
        if (pills) {
          let selectedPill = null;
          pills.querySelectorAll("[data-variant-pill]").forEach((pill) => {
            const isSelected = pill.dataset.variantPill === select.value;
            pill.classList.toggle("is-selected", isSelected);
            pill.setAttribute("aria-pressed", String(isSelected));
            if (isSelected) selectedPill = pill;
          });
          const choice = root?.querySelector("[data-variant-choice]");
          if (choice && selectedPill) {
            choice.textContent = selectedPill.dataset.variantLabel || "";
          }
        }
      });
    });

    // Pills drive the select rather than replacing it, so the form value and
    // every existing variant behaviour stay in one place.
    document.querySelectorAll("[data-variant-pills]").forEach((group) => {
      const select = document.getElementById(group.dataset.target);
      if (!select) return;

      group.addEventListener("click", (event) => {
        const pill = event.target.closest("[data-variant-pill]");
        if (!pill || pill.dataset.variantPill === select.value) return;
        select.value = pill.dataset.variantPill;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  /*
    Set by setupProductGallery so the variant handler can drive the gallery.
    Choosing a colour used to update the price, the availability and the URL but
    leave the photo on whatever was showing, which read as the wrong product.
  */
  let selectProductMedia = null;

  function setupProductGallery() {
    const gallery = document.querySelector("[data-product-gallery]");
    const thumbs = document.querySelectorAll("[data-media-thumb]");
    if (!gallery || !thumbs.length) return;

    const mediaItems = [...gallery.querySelectorAll("[data-media-id]")];
    // focusSelected is true only for a thumbnail click. Moving focus on a
    // variant change would pull it off the option the user just chose.
    const selectMedia = (mediaId, focusSelected = true) => {
      const selected = mediaItems.find(
        (item) => String(item.dataset.mediaId) === String(mediaId),
      );
      if (!selected) return;

      mediaItems.forEach((item) => {
        const active = item === selected;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-hidden", active ? "false" : "true");
      });
      thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaThumb) === String(mediaId);
        thumb.classList.toggle("is-active", active);
        if (active) thumb.setAttribute("aria-current", "true");
        else thumb.removeAttribute("aria-current");
      });

      if (focusSelected) {
        selected.setAttribute("tabindex", "-1");
        selected.focus({ preventScroll: true });
      }
    };

    // Seed aria-hidden from what the template rendered active, not from index
    // 0 - with a ?variant= link those are not the same slide.
    mediaItems.forEach((item) => {
      item.setAttribute(
        "aria-hidden",
        item.classList.contains("is-active") ? "false" : "true",
      );
    });
    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", (event) => {
        event.preventDefault();
        selectMedia(thumb.dataset.mediaThumb);
      });
    });

    selectProductMedia = selectMedia;
  }

  function setupQuantityControls() {
    document.addEventListener("click", (event) => {
      const minus = event.target.closest("[data-quantity-minus]");
      const plus = event.target.closest("[data-quantity-plus]");
      if (!minus && !plus) return;
      const control = event.target.closest("[data-quantity-control]");
      const input = control?.querySelector("[data-quantity-input]");
      if (!input) return;
      const minimum = Number(input.min || 0);
      const maximum = Number(input.max || 99);
      const current = Number(input.value || 0);
      input.value = String(
        Math.min(maximum, Math.max(minimum, current + (plus ? 1 : -1))),
      );
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch(routes.cartChange || "/cart/change.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ line, quantity }),
    });
    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart.description || "Cart could not be updated.");
    }
    return cart;
  }

  function updateCartUI(cart, changedLine, requestedQuantity) {
    updateCartCount(cart.item_count);
    document
      .querySelectorAll("[data-cart-total]")
      .forEach((node) => (node.textContent = money(cart.total_price)));

    const lineElement = document.querySelector(
      `[data-cart-line][data-line="${changedLine}"]`,
    );
    const item = cart.items[changedLine - 1];
    if (lineElement && item && requestedQuantity !== 0) {
      lineElement.querySelector("[data-quantity-input]").value = item.quantity;
      const price = lineElement.querySelector(".cart-line__price strong");
      if (price) price.textContent = money(item.final_line_price);
    } else if (lineElement) {
      lineElement.remove();
      document.querySelectorAll("[data-cart-line]").forEach((line, index) => {
        line.dataset.line = String(index + 1);
      });
    }

    const progress = document.querySelector("[data-shipping-progress]");
    if (progress) {
      const threshold = Number(progress.dataset.threshold || 0);
      const percent = threshold
        ? Math.min(100, (cart.total_price / threshold) * 100)
        : 100;
      const bar = progress.querySelector(".free-shipping-progress__track i");
      if (bar) bar.style.width = `${percent}%`;
      const heading = progress.querySelector("strong");
      if (heading) {
        heading.textContent =
          cart.total_price >= threshold
            ? "Configured free-shipping threshold reached."
            : `${money(
                threshold - cart.total_price,
              )} from the configured threshold.`;
      }
    }

    if (cart.item_count === 0) window.location.reload();
  }

  function setupCart() {
    const cartRoot = document.querySelector("[data-cart-root]");
    if (!cartRoot) return;
    const message = cartRoot.querySelector("[data-cart-message]");

    const change = debounce(async (input) => {
      const lineElement = input.closest("[data-cart-line]");
      const line = Number(lineElement?.dataset.line || 0);
      if (!line) return;
      try {
        input.disabled = true;
        const requestedQuantity = Number(input.value || 0);
        const cart = await changeCartLine(line, requestedQuantity);
        updateCartUI(cart, line, requestedQuantity);
        announce("Cart updated.");
        if (message) message.textContent = "Cart updated.";
      } catch (error) {
        announce(error.message);
        if (message) message.textContent = error.message;
      } finally {
        input.disabled = false;
      }
    }, 220);

    cartRoot.addEventListener("change", (event) => {
      if (event.target.matches("[data-quantity-input]")) change(event.target);
    });
    cartRoot.addEventListener("click", async (event) => {
      const remove = event.target.closest("[data-cart-remove]");
      if (!remove) return;
      event.preventDefault();
      const lineElement = remove.closest("[data-cart-line]");
      const line = Number(lineElement?.dataset.line || 0);
      if (!line) return;
      try {
        const cart = await changeCartLine(line, 0);
        updateCartUI(cart, line, 0);
        announce("Item removed.");
      } catch (error) {
        announce(error.message);
        if (message) message.textContent = error.message;
      }
    });
  }

  function readStorage(key, fallback = []) {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || "");
      return Array.isArray(value) ? value : fallback;
    } catch (_error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function compareSnapshot(button) {
    const data = button.dataset;
    return {
      id: data.productId,
      handle: data.productHandle,
      url: data.productUrl,
      title: data.productTitle,
      vendor: data.productVendor,
      price: data.productPrice,
      shape: data.productShape,
      dimensions: data.productDimensions,
      weight: data.productWeight,
      swing: data.productSwing,
      twist: data.productTwist,
      handleLength: data.productHandleLength,
      grip: data.productGrip,
      core: data.productCore,
      face: data.productFace,
      construction: data.productConstruction,
      power: data.productPower,
      control: data.productControl,
      spin: data.productSpin,
      sweetSpot: data.productSweetSpot,
      maneuver: data.productManeuver,
      skill: data.productSkill,
      style: data.productStyle,
      warranty: data.productWarranty,
      approval: data.productApproval,
    };
  }

  function syncCompareControls(items = readStorage(COMPARE_KEY)) {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll("[data-compare-toggle]").forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("is-active", active);
      button.textContent = active ? "Compared ✓" : "Compare";
    });
    document
      .querySelectorAll("[data-compare-count]")
      .forEach((node) => (node.textContent = String(items.length)));
  }

  function renderComparison() {
    const mount = document.querySelector("[data-compare-mount]");
    if (!mount) return;
    const items = readStorage(COMPARE_KEY);
    const empty = mount.querySelector("[data-compare-empty]");
    const actions = mount.querySelector("[data-compare-actions]");
    const wrap = mount.querySelector("[data-compare-table]");

    if (!items.length) {
      empty.hidden = false;
      actions.hidden = true;
      wrap.hidden = true;
      wrap.innerHTML = "";
      syncCompareControls(items);
      return;
    }

    const fields = [
      ["Price", "price"],
      ["Brand", "vendor"],
      ["Shape", "shape"],
      ["Dimensions", "dimensions"],
      ["Weight range", "weight"],
      ["Swing weight", "swing"],
      ["Twist weight", "twist"],
      ["Handle length", "handleLength"],
      ["Grip circumference", "grip"],
      ["Core thickness", "core"],
      ["Face material", "face"],
      ["Core construction", "construction"],
      ["Power rating", "power"],
      ["Control rating", "control"],
      ["Spin rating", "spin"],
      ["Sweet-spot rating", "sweetSpot"],
      ["Maneuverability", "maneuver"],
      ["Skill level", "skill"],
      ["Playing style", "style"],
      ["Warranty", "warranty"],
      ["Approval status", "approval"],
    ];

    wrap.innerHTML = `
      <table class="compare-table">
        <thead>
          <tr>
            <th scope="col">Specification</th>
            ${items
              .map(
                (item) => `
                  <th scope="col">
                    <span>${escapeHTML(item.vendor || "")}</span>
                    <a href="${escapeHTML(item.url || "#")}">${escapeHTML(
                      item.title || "Paddle",
                    )}</a>
                    <button type="button" class="text-control" data-compare-remove="${escapeHTML(
                      item.id,
                    )}">Remove</button>
                  </th>`,
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${fields
            .map(
              ([label, key]) => `
                <tr>
                  <th scope="row">${escapeHTML(label)}</th>
                  ${items
                    .map(
                      (item) =>
                        `<td>${escapeHTML(item[key] || "Unavailable")}</td>`,
                    )
                    .join("")}
                </tr>`,
            )
            .join("")}
        </tbody>
      </table>`;
    empty.hidden = true;
    actions.hidden = false;
    wrap.hidden = false;
    syncCompareControls(items);
  }

  function setupCompare() {
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-compare-toggle]");
      const remove = event.target.closest("[data-compare-remove]");
      const clear = event.target.closest("[data-compare-clear]");
      let items = readStorage(COMPARE_KEY);

      if (toggle) {
        const snapshot = compareSnapshot(toggle);
        const existing = items.findIndex(
          (item) => String(item.id) === String(snapshot.id),
        );
        if (existing >= 0) {
          items.splice(existing, 1);
          announce(`${snapshot.title} removed from comparison.`);
        } else {
          if (items.length >= MAX_COMPARE) {
            announce("Comparison is limited to four paddles.");
            return;
          }
          items.push(snapshot);
          announce(`${snapshot.title} added to comparison.`);
          track("product_compared", {
            product_id: String(snapshot.id || ""),
            product_name: snapshot.title || "",
            compare_count: items.length,
          });
        }
        writeStorage(COMPARE_KEY, items);
        syncCompareControls(items);
        renderComparison();
      } else if (remove) {
        items = items.filter(
          (item) => String(item.id) !== String(remove.dataset.compareRemove),
        );
        writeStorage(COMPARE_KEY, items);
        renderComparison();
      } else if (clear) {
        writeStorage(COMPARE_KEY, []);
        renderComparison();
      }
    });
    syncCompareControls();
    renderComparison();
  }

  function setupFinder() {
    const root = document.querySelector("[data-finder-root]");
    const form = root?.querySelector("[data-finder-form]");
    const source = root?.querySelector("[data-finder-products]");
    const results = root?.querySelector("[data-finder-results]");
    if (!root || !form || !source || !results) return;

    let products = [];
    try {
      products = JSON.parse(source.textContent || "[]");
    } catch (_error) {
      products = [];
    }
    const questionCount = form.querySelectorAll("fieldset").length;
    let started = false;
    form.addEventListener("change", () => {
      if (!started) {
        track("quiz_started", { quiz: "paddle_finder" });
        started = true;
      }
      const answered = new Set(
        [...new FormData(form).keys()].filter(Boolean),
      ).size;
      const progress = root.querySelector("[data-finder-progress]");
      if (progress && questionCount) {
        progress.style.setProperty(
          "--progress",
          `${Math.min(100, (answered / questionCount) * 100)}%`,
        );
      }
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const answers = Object.fromEntries(new FormData(form).entries());

      const ranked = window.CourtFinderScoring.rankProducts(products, answers);

      if (!ranked.length) {
        results.innerHTML = `
          <div class="empty-state">
            <strong>No eligible matches.</strong>
            <p>No available paddle fits all three choices yet. Try another budget or check back as the assortment grows.</p>
          </div>`;
      } else {
        results.innerHTML = `
          <div class="section-header">
            <div><span class="eyebrow">Your matches</span><h2>${ranked.length} available ${ranked.length === 1 ? "paddle" : "paddles"} to consider.</h2></div>
          </div>
          <p>Matches use experience, preferred playing feel, and budget only. Verify the exact model's current approval status before sanctioned play.</p>
          <div class="finder-result-grid">
            ${ranked
              .map(
                ({ product, score, reasons, rated }, index) => `
                  <article class="finder-result-card">
                    ${
                      product.image
                        ? `<a class="finder-result-card__media" href="${escapeHTML(
                            product.url,
                          )}" tabindex="-1" aria-hidden="true"><img class="finder-result-card__image" src="${escapeHTML(
                            product.image,
                          )}"${
                            product.imageSrcset
                              ? ` srcset="${escapeHTML(product.imageSrcset)}"`
                              : ""
                          } sizes="(min-width: 1464px) 405px, (min-width: 1100px) 28vw, (min-width: 750px) 42vw, 50vw" loading="lazy" decoding="async" alt=""></a>`
                        : ""
                    }
                    <div class="finder-result-card__top">
                      <span class="finder-result-card__rank">0${index + 1}</span>
                      ${
                        rated
                          ? `<span class="finder-result-card__score" title="How closely this paddle's rated construction fits your answers">${score}<span>% fit</span></span>`
                          : `<span class="finder-result-card__score finder-result-card__score--none">Not rated</span>`
                      }
                    </div>
                    ${
                      // No placeholder when a vendor is missing. "Brand
                      // unavailable" reads as a broken field, and inventing a
                      // brand is worse - the row simply goes away.
                      product.vendor
                        ? `<span class="eyebrow">${escapeHTML(product.vendor)}</span>`
                        : ""
                    }
                    <h3><a href="${escapeHTML(product.url)}">${escapeHTML(
                      product.title,
                    )}</a></h3>
                    ${
                      rated
                        ? `<div class="finder-result-card__meter" role="img" aria-label="${score} percent fit"><span style="width:${score}%"></span></div>`
                        : ""
                    }
                    <ul>${reasons
                      .map((reason) => `<li>${escapeHTML(reason)}</li>`)
                      .join("")}</ul>
                    <div class="finder-result-card__foot">
                      <strong>${escapeHTML(product.priceFormatted)}</strong>
                      <a class="button button--ink" href="${escapeHTML(
                        product.url,
                      )}">View paddle</a>
                    </div>
                  </article>`,
              )
              .join("")}
          </div>`;
      }

      results.hidden = false;
      results.scrollIntoView({ behavior: "smooth", block: "start" });
      track("quiz_completed", {
        quiz: "paddle_finder",
        result_count: ranked.length,
        experience: String(answers.experience || ""),
        preference: String(answers.preference || ""),
        budget: String(answers.budget || ""),
      });
    });
  }

  /*
    Guides are long enough now that a reader arriving from search needs to see
    the shape of the page first. Built from the headings rather than stored in
    each article body, so a future guide gets one without the author doing
    anything. Skipped under three headings, where it would be noise.
  */
  function setupGuideToc() {
    const body = document.querySelector("[data-guide-body]");
    if (!body) return;
    if (body.querySelector(".guide-toc")) return;

    const headings = [...body.querySelectorAll("h2")];
    if (headings.length < 3) return;

    const used = new Set();
    headings.forEach((heading, index) => {
      if (!heading.id) {
        const slug =
          heading.textContent
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 60) || `section-${index + 1}`;
        let id = slug;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${slug}-${n++}`;
        heading.id = id;
      }
      used.add(heading.id);
    });

    const nav = document.createElement("nav");
    nav.className = "guide-toc";
    nav.setAttribute("aria-label", "On this page");
    const label = document.createElement("strong");
    label.textContent = "On this page";
    const list = document.createElement("ol");
    headings.forEach((heading) => {
      const item = document.createElement("li");
      const link = document.createElement("a");
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    });
    nav.append(label, list);

    // After the opening paragraph, not above it: the lede is what tells the
    // reader whether the guide is for them at all.
    const lede = body.querySelector("p");
    if (lede && lede.parentElement === body) lede.after(nav);
    else body.prepend(nav);
  }

  function setupRecentlyViewed() {
    const productRoot = document.querySelector("[data-product-root]");
    if (!productRoot) return;
    if (productRoot.dataset.merchantBlocked === "true") return;
    const id = String(productRoot.dataset.productId || "");
    const title =
      productRoot.querySelector(".product-info h1")?.textContent?.trim() || "";
    const vendor =
      productRoot.querySelector(".product-info .eyebrow")?.textContent?.trim() ||
      "";
    const price =
      productRoot.querySelector("[data-product-price]")?.textContent?.trim() ||
      "";
    const url = window.location.pathname;
    let items = readStorage(RECENT_KEY);
    const previous = items.filter((item) => String(item.id) !== id).slice(0, 4);
    items = [{ id, title, vendor, price, url }, ...previous].slice(0, 8);
    writeStorage(RECENT_KEY, items);

    const mount = document.querySelector("[data-recently-viewed]");
    const section = document.querySelector("[data-recently-viewed-section]");
    if (!mount || !section || !previous.length) return;
    mount.innerHTML = previous
      .map(
        (item) => `
          <article class="content-card">
            <span class="eyebrow">${escapeHTML(item.vendor)}</span>
            <h3><a href="${escapeHTML(item.url)}">${escapeHTML(
              item.title,
            )}</a></h3>
            <strong>${escapeHTML(item.price)}</strong>
            <a href="${escapeHTML(item.url)}">View again →</a>
          </article>`,
      )
      .join("");
    section.hidden = false;
  }

  function setupFiltersAndSort() {
    document.querySelectorAll("[data-filter-input]").forEach((input) => {
      input.addEventListener("change", () => {
        const form = input.closest("form");
        track("filter_used", {
          filter_name: input.name,
          filter_value: input.value,
        });
        form?.requestSubmit();
      });
    });
    document.querySelectorAll("[data-sort-select]").forEach((select) => {
      select.addEventListener("change", () => select.form?.requestSubmit());
    });
  }

  function setupUtilityEvents() {
    const productRoot = document.querySelector("[data-product-root]");
    if (productRoot) {
      track("product_viewed", {
        product_id: productRoot.dataset.productId || "",
        product_handle: productRoot.dataset.productHandle || "",
      });
    }
    document.querySelectorAll("[data-checkout-button]").forEach((button) => {
      button.addEventListener("click", () => track("checkout_started"));
    });
    document.querySelectorAll("[data-bulk-form]").forEach((form) => {
      form.addEventListener("submit", () =>
        track("bulk_order_form_submitted"),
      );
    });
    document.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        track("product_added_to_wishlist", {
          product_id: button.dataset.productId || "",
        });
      });
    });
    document
      .querySelectorAll('form[role="search"]:not([data-predictive-search])')
      .forEach((form) => {
        form.addEventListener("submit", () => {
          const input = form.querySelector('input[name="q"]');
          const query = input?.value?.trim() || "";
          track("search_performed", {
            query_length: Array.from(query).length,
          });
        });
      });
    document.querySelectorAll("[data-share-product]").forEach((button) => {
      button.addEventListener("click", async () => {
        const data = {
          title: document.title,
          text: document.title,
          url: window.location.href,
        };
        try {
          if (navigator.share) await navigator.share(data);
          else {
            await navigator.clipboard.writeText(window.location.href);
            announce("Link copied.");
          }
        } catch (_error) {
          // User cancellation or clipboard restrictions need no error surface.
        }
      });
    });
    document.addEventListener("court-current:purchase-completed", (event) => {
      track("purchase_completed", event.detail || {});
    });
  }

  setupDrawers();
  setupPredictiveSearch();
  setupProductForms();
  setupProductGallery();
  setupQuantityControls();
  setupCart();
  setupCompare();
  setupFinder();
  setupGuideToc();
  setupRecentlyViewed();
  function setupStickyHeader() {
    const header = document.querySelector("[data-site-header]");
    if (!header) return;

    // A zero-height sentinel above the header is cheaper than a scroll
    // listener and stays correct when the announcement bar is toggled.
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;top:0;height:1px;width:100%;";
    header.parentNode.insertBefore(sentinel, header);

    new IntersectionObserver(
      ([entry]) => {
        header.classList.toggle("is-stuck", !entry.isIntersecting);
      },
      { rootMargin: "0px" },
    ).observe(sentinel);
  }

  function setupStickyAtc() {
    const bar = document.querySelector("[data-sticky-atc]");
    const anchor = document.querySelector("[data-add-to-cart]");
    if (!bar || !anchor) return;

    // Reveal the bar only once the real add-to-cart has scrolled away, so it
    // never competes with the button it is standing in for.
    new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle("is-visible", !entry.isIntersecting);
      },
      { rootMargin: "0px 0px -40px 0px" },
    ).observe(anchor);
  }

  function setupPaddleMatcher() {
    const root = document.querySelector("[data-matcher-root]");
    if (!root) return;
    const grid = document.querySelector("[data-collection-root] .product-grid");
    if (!grid) return;

    const AXES = ["power", "control", "spin", "forgiveness", "maneuverability"];
    // 1..10 on each axis, so the worst possible distance is 9 per axis.
    const WORST = AXES.length * 9;

    const cards = [...grid.querySelectorAll("[data-product-card]")].map((el) => {
      const ratings = {};
      let rated = true;
      AXES.forEach((axis) => {
        const raw = parseInt(el.dataset[`rating${axis[0].toUpperCase()}${axis.slice(1)}`], 10);
        if (Number.isNaN(raw)) rated = false;
        ratings[axis] = raw;
      });
      return { el, ratings, rated, order: [...grid.children].indexOf(el) };
    });

    // Nothing to rank against unless at least two cards carry a full rating set.
    if (cards.filter((c) => c.rated).length < 2) return;
    root.hidden = false;

    const badgeFor = (card) => {
      let badge = card.el.querySelector("[data-matcher-badge]");
      if (!badge) {
        badge = document.createElement("p");
        badge.className = "product-card__match";
        badge.setAttribute("data-matcher-badge", "");
        const body = card.el.querySelector(".product-card__body") || card.el;
        body.prepend(badge);
      }
      return badge;
    };

    const targets = Object.fromEntries(AXES.map((a) => [a, 5]));
    let touched = false;

    function apply() {
      cards.forEach((card) => {
        if (!card.rated) {
          card.score = -1;
          return;
        }
        const distance = AXES.reduce(
          (sum, axis) => sum + Math.abs(targets[axis] - card.ratings[axis]),
          0,
        );
        card.score = Math.round((1 - distance / WORST) * 100);
      });

      const ranked = [...cards].sort((a, b) =>
        b.score === a.score ? a.order - b.order : b.score - a.score,
      );

      const frag = document.createDocumentFragment();
      ranked.forEach((card) => {
        if (touched) {
          const badge = badgeFor(card);
          badge.textContent = card.rated ? `${card.score}% match` : "Not rated";
          badge.classList.toggle("is-top", touched && card.rated && card === ranked[0]);
        }
        frag.appendChild(card.el);
      });
      grid.appendChild(frag);

      const status = root.querySelector("[data-matcher-status]");
      if (status && touched) {
        const best = ranked.find((c) => c.rated);
        const title = best?.el.querySelector(".product-card__title, h3, h2");
        status.textContent = best
          ? `Best match: ${title ? title.textContent.trim() : "top result"} at ${best.score}%.`
          : "";
      }
    }

    root.addEventListener("input", (event) => {
      const slider = event.target.closest("[data-matcher-slider]");
      if (!slider) return;
      const axis = slider.dataset.matcherSlider;
      targets[axis] = parseInt(slider.value, 10);
      const out = root.querySelector(`[data-matcher-value="${axis}"]`);
      if (out) out.textContent = slider.value;
      if (!touched) {
        touched = true;
        root.querySelector("[data-matcher-reset]")?.removeAttribute("hidden");
        track("paddle_matcher_used", { axis });
      }
      apply();
    });

    root.querySelector("[data-matcher-reset]")?.addEventListener("click", () => {
      AXES.forEach((axis) => {
        targets[axis] = 5;
        const slider = root.querySelector(`[data-matcher-slider="${axis}"]`);
        const out = root.querySelector(`[data-matcher-value="${axis}"]`);
        if (slider) slider.value = "5";
        if (out) out.textContent = "5";
      });
      touched = false;
      cards.forEach((card) => card.el.querySelector("[data-matcher-badge]")?.remove());
      const status = root.querySelector("[data-matcher-status]");
      if (status) status.textContent = "";
      root.querySelector("[data-matcher-reset]")?.setAttribute("hidden", "");
      apply();
    });

    // The homepage teaser hands its targets over in the query string. Arriving
    // with them set is treated exactly like having moved the sliders here, so
    // the grid is already ranked on first paint.
    const params = new URLSearchParams(window.location.search);
    let preset = false;
    AXES.forEach((axis) => {
      const raw = parseInt(params.get(axis), 10);
      if (Number.isNaN(raw) || raw < 1 || raw > 10) return;
      targets[axis] = raw;
      preset = true;
      const slider = root.querySelector(`[data-matcher-slider="${axis}"]`);
      const out = root.querySelector(`[data-matcher-value="${axis}"]`);
      if (slider) slider.value = String(raw);
      if (out) out.textContent = String(raw);
    });
    if (preset) {
      touched = true;
      root.querySelector("[data-matcher-reset]")?.removeAttribute("hidden");
      apply();
    }
  }

  /* Homepage teaser: collects the same five targets and rewrites its CTA so the
     link carries them to the collection. Never reorders anything itself. */
  function setupMatcherTeaser() {
    const root = document.querySelector("[data-teaser-root]");
    const cta = document.querySelector("[data-teaser-cta]");
    if (!root || !cta) return;

    const base = cta.dataset.teaserBase || cta.getAttribute("href");
    const targets = {};
    let touched = false;

    function sync() {
      const params = new URLSearchParams();
      Object.keys(targets).forEach((axis) => params.set(axis, targets[axis]));
      const query = params.toString();
      cta.setAttribute("href", query ? `${base}?${query}` : base);
    }

    root.addEventListener("input", (event) => {
      const slider = event.target.closest("[data-teaser-slider]");
      if (!slider) return;
      const axis = slider.dataset.teaserSlider;
      targets[axis] = parseInt(slider.value, 10);
      const out = root.querySelector(`[data-teaser-value="${axis}"]`);
      if (out) out.textContent = slider.value;
      if (!touched) {
        touched = true;
        track("paddle_matcher_teaser_used", { axis });
      }
      sync();
    });
  }

  setupFiltersAndSort();
  setupUtilityEvents();
  setupStickyHeader();
  setupStickyAtc();
  setupPaddleMatcher();
  setupMatcherTeaser();
})();
